import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from 'Compass/profile-lab/services/db';
import { getUserLid } from 'Compass/profile-lab/hooks/useUserLid';
import { formatAbsoluteTime } from 'Compass/profile-lab/utils/timeFormatting';
import { getApiHeaders } from 'Compass/profile-lab/services/headers';
import { getCommand, Message } from 'Compass/profile-lab/commands/chatCommands';
import { useSelectionContext } from 'Compass/profile-lab/contexts/SelectionContext';
import {
  ChatContainer,
  ChatForm,
  ChatMessageAssistant,
  ChatMessageClickable,
  ChatMessageContent,
  ChatMessages,
  ChatMessageSystem,
  ChatMessageUser,
  ChatTextArea
} from 'Compass/profile-lab/styles/chat';

declare const WEBSERVER_BASE_URL: string;

const ProfileLabChatPage: React.FC = () => {
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [chatMode, setChatMode] = useState<'adhoc' | 'stage'>('adhoc');
  const { isProcessing, setIsProcessing, setProcessingStatus } = useSelectionContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentMessageRef = useRef<Message | null>(null);
  const processedUrlRef = useRef<string>('');

  useEffect(() => {
    const loadUI = async () => {
      await db.init();

      const chatConfig = await db.getChatConfig();
      setChatMode(chatConfig.mode);

      const uiMessages = await db.getUIMessages();
      const loadedMessages = uiMessages.map(msg => ({
        content: msg.content,
        sender: msg.sender,
        timestamp: msg.timestamp
      }));
      setMessages(loadedMessages);

      if (loadedMessages.length === 0) {
        const welcomeMsg: Message = {
          content: 'Welcome to Profile Lab Chat! Type /help to see available commands.',
          sender: 'system',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, welcomeMsg]);
        await db.addUIMessage(welcomeMsg);
      }

      setIsInitialized(true);
    };

    void loadUI();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCommand = async (message: string): Promise<boolean> => {
    const parts = message.trim().split(/\s+/);
    const commandName = parts[0];
    const args = parts.slice(1);

    const command = getCommand(commandName);
    if (command) {
      await command.execute(args, message, { setMessages, db });
      const chatConfig = await db.getChatConfig();
      setChatMode(chatConfig.mode);
      return true;
    }

    return false;
  };

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isProcessing) return;

    setInput('');

    if (message.startsWith('/')) {
      const userMessage: Message = {
        content: message,
        sender: 'user',
        timestamp: Date.now()
      };
      setMessages((prev) => [...prev, userMessage]);
      await db.addUIMessage(userMessage);

      const handled = await handleCommand(message);
      if (!handled) {
        const systemMsg: Message = {
          content: `Unknown command: ${message.split(/\s+/)[0]}. Type /help for available commands.`,
          sender: 'system',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, systemMsg]);
        await db.addUIMessage(systemMsg);
      }
      return;
    }

    const userMessage: Message = {
      content: message,
      sender: 'user',
      timestamp: Date.now()
    };
    setMessages((prev) => [...prev, userMessage]);
    await db.addChatMessage(userMessage);
    await db.addUIMessage(userMessage);
    currentMessageRef.current = null;

    setIsProcessing(true);

    try {
      const userLid = await getUserLid();

      const config = await db.getChatConfig();
      const selectionState = await db.getSelectionState();
      const photoIds = [...selectionState.selected, ...selectionState.grow];

      if (photoIds.length > 400) {
        const warningMsg: Message = {
          content: `Warning: You have ${photoIds.length} photos selected. Only the 400 most recent photos will be used.`,
          sender: 'system',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, warningMsg]);
        await db.addUIMessage(warningMsg);
      }

      let endpoint = '/api/dazzle/profile-lab/chat/process';
      let requestBody: Record<string, unknown>;

      if (config.mode === 'stage') {
        const workingStage = await db.getWorkingStage();
        if (!workingStage) {
          const errorMsg: Message = {
            content: 'No stage loaded in stage mode. Use /work <stage_id> first.',
            sender: 'system',
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, errorMsg]);
          await db.addUIMessage(errorMsg);
          setIsProcessing(false);
          return;
        }

        endpoint = '/api/dazzle/profile-lab/stage/preview';
        requestBody = {
          user_lid: userLid,
          stage_id: workingStage.stageId,
          template: workingStage.template,
          datasets: workingStage.datasets,
          questions: message,
          combiner_template: workingStage.combinerTemplate,
          chunk_size: workingStage.chunkSize,
          template_only: workingStage.templateOnly,
          model_type: workingStage.modelType,
          photo_selection_config: workingStage.photoSelectionConfig ? {
            max_months: workingStage.photoSelectionConfig.maxMonths,
            limit: workingStage.photoSelectionConfig.limit,
            only_self_identity: workingStage.photoSelectionConfig.onlySelfIdentity,
            has_faces: workingStage.photoSelectionConfig.hasFaces,
          } : null,
          photo_lids: photoIds
        };
      } else {
        const chatHistory = await db.getChatHistory();
        const datasetState = await db.getDatasetState();
        const enabledDatasets = datasetState.datasets.filter(d => d.enabled && d.data);

        requestBody = {
          user_lid: userLid,
          message,
          chat_history: config.sendHistory ? chatHistory.filter(m => m.sender !== 'system').map(m => ({ content: m.content, sender: m.sender })) : [],
          photo_lids: photoIds,
          model_type: config.modelType
        };

        if (enabledDatasets.length > 0) {
          const datasets: Record<string, string> = {};
          for (const dataset of enabledDatasets) {
            datasets[dataset.name] = dataset.data!;
          }
          requestBody.datasets = datasets;
        }
      }

      const response = await fetch(`${WEBSERVER_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(requestBody)
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let assistantMessage: Message | null = null;
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.heartbeat && data.status) {
              setProcessingStatus(data.status);
            }

            if (data.content) {
              fullResponse += data.content;

              if (!assistantMessage) {
                assistantMessage = { content: data.content, sender: 'assistant', timestamp: Date.now() };
                setMessages(prev => [...prev, assistantMessage!]);
              } else {
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg && lastMsg.sender === 'assistant') {
                    lastMsg.content = fullResponse;
                  }
                  return updated;
                });
              }
            }

            if (data.done && fullResponse) {
              await db.addChatMessage({ content: fullResponse, sender: 'assistant', timestamp: Date.now() });
              await db.addUIMessage({ content: fullResponse, sender: 'assistant', timestamp: Date.now() });
            }

            if (data.error) {
              const errorMsg: Message = { content: `Error: ${data.error}`, sender: 'system', timestamp: Date.now() };
              setMessages(prev => [...prev, errorMsg]);
              await db.addUIMessage(errorMsg);
            }
          }
        }
      }
    } catch (error) {
      const errorMsg: Message = { content: `Error: ${error}`, sender: 'system', timestamp: Date.now() };
      setMessages(prev => [...prev, errorMsg]);
      await db.addUIMessage(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, setIsProcessing, setProcessingStatus]);

  useEffect(() => {
    if (!isInitialized) return;

    const currentUrl = location.pathname + location.search;
    if (processedUrlRef.current === currentUrl) return;

    const params = new URLSearchParams(location.search);
    const action = params.get('action');

    if (!action) return;

    processedUrlRef.current = currentUrl;

    if (action === 'preview') {
      db.getWorkingStage().then((workingStage) => {
        if (workingStage && workingStage.questions) {
          setTimeout(() => {
            void sendMessage(workingStage.questions!);
          }, 500);
        }
      });
    }
  }, [isInitialized, location.pathname, location.search, sendMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.altKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const handleQuestionClick = (event: React.MouseEvent<HTMLDivElement>, message: Message) => {
    if (!message.content.includes('→ ')) return;

    const messageDiv = event.currentTarget;
    const messageRect = messageDiv.getBoundingClientRect();
    const clickY = event.clientY - messageRect.top;

    const lines = message.content.split('\n');
    const lineHeight = messageRect.height / lines.length;
    const clickedLineIndex = Math.floor(clickY / lineHeight);

    if (clickedLineIndex >= 0 && clickedLineIndex < lines.length) {
      const clickedLine = lines[clickedLineIndex];
      if (clickedLine.startsWith('→ ')) {
        const cleanQuestion = clickedLine.slice(2);
        void sendMessage(cleanQuestion);
      }
    }
  };

  const handleUserMessageClick = (message: Message) => {
    if (message.sender === 'user' && !message.content.startsWith('/')) {
      if (input === '') {
        setInput(message.content);
      }
    }
  };


  return (
    <ChatContainer>
      <ChatMessages>
        {messages.map((msg, idx) => {
          const isClickable = (msg.sender === 'user' && !msg.content.startsWith('/')) || msg.content.includes('→ ');
          const MessageComponent = msg.sender === 'user' ? ChatMessageUser : msg.sender === 'assistant' ? ChatMessageAssistant : ChatMessageSystem;
          const WrapperComponent = isClickable ? ChatMessageClickable : Fragment;

          return (
            <WrapperComponent key={idx}>
              <MessageComponent
                onClick={(e) => {
                  if (msg.sender === 'system' && msg.content.includes('→ ')) {
                    handleQuestionClick(e, msg);
                  } else if (msg.sender === 'user') {
                    handleUserMessageClick(msg);
                  }
                }}
              >
                <span style={{ opacity: 0.5, fontSize: '0.85em', marginRight: '8px' }}>{formatAbsoluteTime(msg.timestamp)}</span>
                {msg.sender === 'user' && <strong>You: </strong>}
                {msg.sender === 'assistant' && <strong>Assistant: </strong>}
                {msg.sender === 'system' && <strong>System: </strong>}
                <ChatMessageContent>{msg.content}</ChatMessageContent>
              </MessageComponent>
            </WrapperComponent>
          );
        })}
        <div ref={messagesEndRef} />
      </ChatMessages>
      <ChatForm as="form" onSubmit={handleSubmit}>
        <ChatTextArea
          placeholder={chatMode === 'stage' ? "Stage preview mode - template will be applied (Enter to send, Alt+Enter for new line)" : "Type a message... (Enter to send, Alt+Enter for new line)"}
          autoComplete="off"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isProcessing}
          rows={3}
        />
      </ChatForm>
    </ChatContainer>
  );
};

export default ProfileLabChatPage;
